import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Providers } from 'generated/graphql-types';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ServerOption } from 'src/app/common-components/entity-select/server/models/server-option';
import {
   getProviderDisplayName,
   getProviders,
   providerSearch,
} from 'src/app/common-components/helpers/provider-helper';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { AccountOption } from 'src/app/pages/account-register/models/account-option.interface';
import { ActiveCallsOrderingOptions, ActiveCallsRegisterOptions } from '../../models/active-calls-register-options';
import { ActiveCallsRegisterListItem } from '../../models/active-calls-register-results.model';
import { HangUpCallsPrivileges } from '../../models/hang-up-calls.privileges';
import { toggleAutoRefresh } from '../../state/system-active-calls/system-active-calls.actions';
import { selectQuickFilters } from '../../state/system-active-calls/system-active-calls.reducer';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-system-active-calls-register-view',
   styleUrls: ['./system-active-calls-register-view.component.scss'],
   templateUrl: './system-active-calls-register-view.component.html',
   standalone: false,
})
export class SystemActiveCallsRegisterViewComponent
   extends EntityRegisterView<ActiveCallsRegisterListItem, ActiveCallsRegisterOptions, ActiveCallsOrderingOptions>
   implements OnInit, OnDestroy
{
   protected selectQuickFilters(): (state: object) => ActiveCallsRegisterOptions | null {
      return selectQuickFilters;
   }

   protected override onQuickFiltersInit(quickFilters: ActiveCallsRegisterOptions): void {
      this.selectedAccount = quickFilters.account;
   }
   @Input() includeInactive: boolean;

   @Input() hangupActiveCallsPrivileges: HangUpCallsPrivileges;

   @Input() isSystemUser: boolean;

   @Output() openDialog = new EventEmitter<{ callLogs: any }>();

   @Output() hangupActiveCall = new EventEmitter<{ activeCall: ActiveCallsRegisterListItem }>();

   selectedAccount: AccountOption | undefined = undefined;

   selectedServer: ServerOption | undefined = undefined;

   isAutoRefresh = true;

   providers = getProviders();

   selectedProvider: Providers | null;

   formatProvider = getProviderDisplayName;

   private minuteInterval: any;

   private tenSecondInterval: any;

   private destroyed$ = new Subject<void>();

   constructor(private cdRef: ChangeDetectorRef, public store: Store) {
      super(store);

      interval(1000)
         .pipe(takeUntil(this.destroyed$))
         .subscribe(() => {
            this.cdRef.markForCheck();
         });
   }

   ngOnInit() {
      super.ngOnInit();
      this.startMinuteTimer();
      this.startTenSecondTimer();
      this.currentTime = new Date();

      this.cdRef.markForCheck();
   }

   getRouterLink(id: number) {
      return '';
   }

   autoRefreshToggleClicked(isToggled: boolean) {
      this.isAutoRefresh = isToggled;
      this.store.dispatch(toggleAutoRefresh({ isAutoRefresh: isToggled }));
   }

   onAccountSelected(account: AccountOption) {
      this.registerOptions = {
         ...this.registerOptions,
         account: account.name ? { ...account, name: account.name } : undefined,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   providerFilterChange(provider: Providers | null) {
      this.registerOptions = {
         ...this.registerOptions,
         providerId: provider ?? undefined,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   onOpenDialog(callLogs: any) {
      this.openDialog.emit({ callLogs: callLogs });
   }

   onHangupActiveCall(activeCall: ActiveCallsRegisterListItem) {
      this.hangupActiveCall.emit({ activeCall: activeCall });
   }

   onServerSelected(server: ServerOption) {
      this.registerOptions = {
         ...this.registerOptions,
         serverName: server ? server.serverName : undefined,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   ngOnDestroy() {
      this.destroyed$.next();
      this.stopMinuteTimer();
   }

   providerDisplayWith(option: Providers): string {
      return `${option ?? ''}`;
   }

   providerSearch(searchText: string) {
      this.providers = providerSearch(searchText);
   }

   providerReset() {
      this.providers = getProviders();
   }

   currentTime: Date;

   private startMinuteTimer() {
      this.minuteInterval = setInterval(() => {
         this.cdRef.markForCheck();
      }, 60000);
   }

   private startTenSecondTimer() {
      this.tenSecondInterval = setInterval(() => {
         this.currentTime = new Date();
         this.cdRef.markForCheck();
      }, 10000);
   }

   private stopMinuteTimer() {
      if (this.minuteInterval) {
         clearInterval(this.minuteInterval);
         clearInterval(this.tenSecondInterval);
      }
   }
}
